/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-24
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Pagination,
  Alert,
  CircularProgress,
  InputAdornment,
  Chip,
  Snackbar,
  Stack,
} from '@mui/material';
import { Search, FilterList, Refresh } from '@mui/icons-material';

// Add local dependencies.
import {
  JoinRequestListProps,
  JoinRequestListParams,
  JoinRequestStatus,
} from '../../../types/joinRequest';
import { StudioRole } from '../../../types/studio';
import {
  useGetJoinRequestsQuery,
  useApproveJoinRequestMutation,
  useDenyJoinRequestMutation,
} from '../../../features/studios/studiosApi';
import { useStudioPermissions } from '../../../hooks/useStudioPermissions';
import { getApiErrorMessage } from '../../../utils/errorHandling';
import { getRoleDisplayName } from '../../../utils/permissions';
import JoinRequestCard from './JoinRequestCard';

/**
 * JoinRequestList component displays and manages join requests for a studio.
 * Provides filtering, searching, and pagination capabilities for administrators.
 */
const JoinRequestList = ({
  studioId,
  searchParams: initialSearchParams,
  onRequestUpdate,
}: JoinRequestListProps) => {
  const [searchParams, setSearchParams] = useState<JoinRequestListParams>({
    studioId,
    page: 1,
    pageSize: 10,
    status: JoinRequestStatus.PENDING, // Default to showing only pending requests
    ...initialSearchParams,
  });
  const [searchTerm, setSearchTerm] = useState(searchParams.search || '');

  const { permissions } = useStudioPermissions();
  const [approveRequest, { isLoading: isApproving }] =
    useApproveJoinRequestMutation();
  const [denyRequest, { isLoading: isDenying }] = useDenyJoinRequestMutation();
  // Approve/deny feedback ('general' successes are not shown globally).
  const [notice, setNotice] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);

  const {
    data: joinRequestsData,
    isLoading,
    error,
    refetch,
  } = useGetJoinRequestsQuery(searchParams, {
    skip: !permissions?.canManageMembers,
  });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = () => {
    setSearchParams(prev => ({
      ...prev,
      search: searchTerm.trim() || undefined,
      page: 1,
    }));
  };

  const handleFilterChange =
    (field: keyof JoinRequestListParams) => (event: any) => {
      const { value } = event.target;
      setSearchParams(prev => ({
        ...prev,
        [field]: value || undefined,
        page: 1,
      }));
    };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setSearchParams(prev => ({ ...prev, page }));
  };

  const handleClearFilters = () => {
    setSearchParams({
      studioId,
      page: 1,
      pageSize: 10,
      status: JoinRequestStatus.PENDING, // Reset to default pending status
    });
    setSearchTerm('');
  };

  const handleRequestUpdate = (updatedRequest: any) => {
    onRequestUpdate?.(updatedRequest);
    refetch();
  };

  // Check permissions
  if (!permissions?.canManageMembers) {
    return (
      <Alert severity="warning">
        You do not have permission to view join requests for this studio.
      </Alert>
    );
  }

  const requests = joinRequestsData?.results || [];
  const totalPages = joinRequestsData?.count
    ? Math.ceil(joinRequestsData.count / (searchParams.pageSize || 10))
    : 0;

  const activeFiltersCount = [
    searchParams.search,
    searchParams.status,
    searchParams.requestedRole,
    searchParams.dateFrom,
    searchParams.dateTo,
  ].filter(Boolean).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Join Requests</Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => refetch()}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </Box>

      {/* Search and Filters */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Search Bar */}
        <TextField
          placeholder="Search by requester name or message..."
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyPress={e => e.key === 'Enter' && handleSearchSubmit()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <Button size="small" onClick={handleSearchSubmit}>
                  Search
                </Button>
              </InputAdornment>
            ),
          }}
          fullWidth
        />

        {/* Filters Row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1fr 1fr 1fr auto',
            },
            gap: 2,
            alignItems: 'center',
          }}
        >
          {/* Status Filter */}
          <FormControl size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={searchParams.status || ''}
              onChange={handleFilterChange('status')}
              label="Status"
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value={JoinRequestStatus.PENDING}>
                Pending (Default)
              </MenuItem>
              <MenuItem value={JoinRequestStatus.APPROVED}>Approved</MenuItem>
              <MenuItem value={JoinRequestStatus.REJECTED}>Rejected</MenuItem>
            </Select>
          </FormControl>

          {/* Role Filter */}
          <FormControl size="small">
            <InputLabel>Requested Role</InputLabel>
            <Select
              value={searchParams.requestedRole || ''}
              onChange={handleFilterChange('requestedRole')}
              label="Requested Role"
            >
              <MenuItem value="">All Roles</MenuItem>
              {Object.values(StudioRole).map(role => (
                <MenuItem key={role} value={role}>
                  {getRoleDisplayName(role)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Page Size */}
          <FormControl size="small">
            <InputLabel>Per Page</InputLabel>
            <Select
              value={searchParams.pageSize || 10}
              onChange={handleFilterChange('pageSize')}
              label="Per Page"
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>

          {/* Clear Filters */}
          {activeFiltersCount > 0 && (
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={handleClearFilters}
              size="small"
            >
              Clear ({activeFiltersCount})
            </Button>
          )}
        </Box>

        {/* Active Filters Display */}
        {activeFiltersCount > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {searchParams.search && (
              <Chip
                label={`Search: "${searchParams.search}"`}
                size="small"
                onDelete={() => {
                  setSearchParams(prev => ({
                    ...prev,
                    search: undefined,
                    page: 1,
                  }));
                  setSearchTerm('');
                }}
              />
            )}
            {searchParams.status && (
              <Chip
                label={`Status: ${searchParams.status}`}
                size="small"
                onDelete={() =>
                  setSearchParams(prev => ({
                    ...prev,
                    status: undefined,
                    page: 1,
                  }))
                }
              />
            )}
            {searchParams.requestedRole && (
              <Chip
                label={`Role: ${getRoleDisplayName(searchParams.requestedRole)}`}
                size="small"
                onDelete={() =>
                  setSearchParams(prev => ({
                    ...prev,
                    requestedRole: undefined,
                    page: 1,
                  }))
                }
              />
            )}
          </Stack>
        )}
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error">
          Failed to load join requests. Please try again.
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Results */}
      {!isLoading && !error && (
        <>
          {/* Results Summary */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {joinRequestsData?.count || 0} request
              {joinRequestsData?.count !== 1 ? 's' : ''} found
            </Typography>
            {totalPages > 1 && (
              <Typography variant="body2" color="text.secondary">
                Page {searchParams.page} of {totalPages}
              </Typography>
            )}
          </Box>

          {/* Request Cards */}
          {requests.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {requests.map(request => (
                <JoinRequestCard
                  key={request.id}
                  request={request}
                  onApprove={async (requestId, role) => {
                    try {
                      await approveRequest({
                        requestId,
                        assignedRole: role,
                      }).unwrap();

                      setNotice({
                        severity: 'success',
                        message: 'Join request approved successfully',
                      });

                      handleRequestUpdate(request);
                    } catch (err) {
                      setNotice({
                        severity: 'error',
                        message: getApiErrorMessage(
                          err,
                          'Failed to approve join request. Please try again.'
                        ),
                      });
                    }
                  }}
                  onDeny={async (requestId, reason) => {
                    try {
                      await denyRequest({
                        requestId,
                        reason: reason || 'Request denied by administrator',
                        allowResubmission: false,
                      }).unwrap();

                      setNotice({
                        severity: 'success',
                        message: 'Join request denied',
                      });

                      handleRequestUpdate(request);
                    } catch (err) {
                      setNotice({
                        severity: 'error',
                        message: getApiErrorMessage(
                          err,
                          'Failed to deny join request. Please try again.'
                        ),
                      });
                    }
                  }}
                  onView={_requestId => {
                    // TODO: Implement view request details functionality
                  }}
                  showActions={!isApproving && !isDenying}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No join requests found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {activeFiltersCount > 0
                  ? 'Try adjusting your filters to see more results.'
                  : 'No one has requested to join this studio yet.'}
              </Typography>
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={searchParams.page || 1}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={!!notice}
        autoHideDuration={6000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotice(null)}
          severity={notice?.severity}
          sx={{ width: '100%' }}
        >
          {notice?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JoinRequestList;
